import { useBreadcrumbs } from "#routers/breadcrumbsHelper";
import { useParams } from "#routers/hooks";
import { AccessDeniedLayout } from "#src/batteries-included-components/Layouts/Authorization/AccessDenied";
import {
  useIsFeatureAvailable,
  useLegacyIsFeatureAvailable,
} from "#src/contexts/AuthenticatedContext.helpers";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Page,
  Tooltip,
  useToast,
} from "@validereinc/common-components";
import {
  DashboardRefreshFailureReasons,
  DashboardRefreshResults,
  type DashboardRefreshTriggerResultType,
  Insights,
  InsightsAdapterQueryKeys,
  type QuickSightDashboardDetailType,
} from "@validereinc/domain";
import { FeatureFlags, PermissionsMap } from "@validereinc/domain-controllers";
import * as QuickSightEmbedding from "amazon-quicksight-embedding-sdk";
import classNames from "classnames/bind";
import React, { useEffect, useRef } from "react";
import { INSIGHTS_DETAILS_DASHBOARD_BREADCRUMB } from ".";
import { INSIGHTS_BREADCRUMB } from "../..";
import { DASHBOARDS_BREADCRUMB } from "../../..";
import {
  getDashboardLastRefreshedAtText,
  getDashboardRefreshAvailableAtText,
  getDashboardRefreshToastText,
  getLastManuallyRefreshedAtFromRetryAfter,
} from "./InsightsDetailsPage.helpers";
import styles from "./InsightsDetailsPage.module.scss";
import { useDashboardRefreshCooldown } from "./useDashboardRefreshCooldown";

const cx = classNames.bind(styles);

export const InsightsDetailsPage = () => {
  const [isInsightsAvailableLegacy] = useLegacyIsFeatureAvailable({
    featureFlagQuery: ["core/insights_dashboard"],
    permissionQuery: ["core:insights"],
    featureId: "dashboards",
  });
  const [isInsightsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "dashboards:insights",
    permissionQuery: "insights_dashboards:read",
  });
  const iframeRef = useRef(null);
  const { insightsId } = useParams<{ insightsId: string }>();

  const [shouldUseV2Insights] = useIsFeatureAvailable({
    featureFlagQuery: {
      $and: [FeatureFlags.DashboardsManageInsights],
    },
    permissionQuery: PermissionsMap.InsightsDashboardsRead,
  });

  const { data: dashboardDetailsData, isLoading: isLoadingDashboards } =
    useQuery({
      queryKey: InsightsAdapterQueryKeys.getDashboardDetails({ insightsId }),
      queryFn: () => Insights.getDashboardDetails({ insightsId }),
      enabled: Boolean(!shouldUseV2Insights && insightsId),
    });

  const { data: dashboardDetailsDataV2, isLoading: isLoadingDashboardsV2 } =
    useQuery({
      queryKey: InsightsAdapterQueryKeys.getDashboardDetailsV2({ insightsId }),
      queryFn: () => Insights.getDashboardDetailsV2({ insightsId }),
      enabled: Boolean(shouldUseV2Insights && insightsId),
    });

  const dashboardDetails = shouldUseV2Insights
    ? dashboardDetailsDataV2
    : dashboardDetailsData;
  const isLoading = shouldUseV2Insights
    ? isLoadingDashboardsV2
    : isLoadingDashboards;

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { lastManuallyRefreshedAt, refreshAvailableAt, startCooldownFrom } =
    useDashboardRefreshCooldown({
      insightsId,
      serverLastManuallyRefreshedAt:
        dashboardDetails?.dashboard?.last_manually_refreshed_at,
    });

  const startCooldownAndUpdateCachedDetails = (lastRefreshedAt: string) => {
    startCooldownFrom(lastRefreshedAt);
    queryClient.setQueryData<QuickSightDashboardDetailType>(
      shouldUseV2Insights
        ? InsightsAdapterQueryKeys.getDashboardDetailsV2({ insightsId })
        : InsightsAdapterQueryKeys.getDashboardDetails({ insightsId }),
      (cachedDetails) =>
        cachedDetails && {
          ...cachedDetails,
          dashboard: {
            ...cachedDetails.dashboard,
            last_manually_refreshed_at: lastRefreshedAt,
          },
        }
    );
  };

  const handleRefreshResult = (result: DashboardRefreshTriggerResultType) => {
    toast.push(
      {
        intent:
          result.result === DashboardRefreshResults.SUCCESS
            ? "success"
            : "error",
        description: getDashboardRefreshToastText(result),
      },
      { isDismissible: true }
    );

    if (result.result === DashboardRefreshResults.SUCCESS) {
      startCooldownAndUpdateCachedDetails(new Date().toISOString());
      return;
    }

    if (result.reason === DashboardRefreshFailureReasons.COOLDOWN_ACTIVE) {
      startCooldownAndUpdateCachedDetails(
        getLastManuallyRefreshedAtFromRetryAfter(
          result.retryAfterSeconds,
          Date.now()
        )
      );
    }
  };

  const refreshDashboardDatasetsMutation = useMutation({
    mutationFn: () =>
      shouldUseV2Insights
        ? Insights.refreshDashboardDatasetsV2({ insightsId })
        : Insights.refreshDashboardDatasets({ insightsId }),
    onSuccess: handleRefreshResult,
    onError: () =>
      toast.push(
        {
          intent: "error",
          description: getDashboardRefreshToastText({
            result: DashboardRefreshResults.FAILURE,
            reason: DashboardRefreshFailureReasons.GENERAL_FAILURE,
          }),
        },
        { isDismissible: true }
      ),
  });

  useEffect(() => {
    async function embedDashboard() {
      if (!dashboardDetails?.embed_url || !iframeRef.current) {
        return;
      }

      const embeddingContext =
        await QuickSightEmbedding.createEmbeddingContext();

      const frameOptions = {
        url: dashboardDetails.embed_url,
        container: iframeRef.current,
        withIframePlaceholder: true,
        resizeHeightOnSizeChangedEvent: true,
      };

      const contentOptions = {
        toolbarOptions: {
          export: true,
          undoRedo: false,
          reset: false,
        },
      };

      await embeddingContext.embedDashboard(frameOptions, contentOptions);
    }

    embedDashboard();
  }, [dashboardDetails?.embed_url]);

  if (!isInsightsAvailable && !isInsightsAvailableLegacy) {
    return <AccessDeniedLayout />;
  }

  return (
    <Page
      isLoading={isLoading}
      title={dashboardDetails?.dashboard?.name}
      breadcrumbs={useBreadcrumbs(
        [
          DASHBOARDS_BREADCRUMB,
          INSIGHTS_BREADCRUMB,
          INSIGHTS_DETAILS_DASHBOARD_BREADCRUMB,
        ],
        { 2: dashboardDetails?.dashboard.name }
      )}
      category="Insights"
      renderMeta={({ MetaSegments }) => (
        <MetaSegments
          values={[getDashboardLastRefreshedAtText(lastManuallyRefreshedAt)]}
        />
      )}
      actionRow={[
        refreshAvailableAt ? (
          <Tooltip
            key="refresh-dashboard-datasets"
            content={getDashboardRefreshAvailableAtText(refreshAvailableAt)}
          >
            <Button
              variant="outline"
              icon="clock-counter-clockwise"
              isLoading={refreshDashboardDatasetsMutation.isLoading}
              disabled
            >
              Refresh
            </Button>
          </Tooltip>
        ) : (
          <Button
            key="refresh-dashboard-datasets"
            variant="outline"
            icon="clock-counter-clockwise"
            isLoading={refreshDashboardDatasetsMutation.isLoading}
            onClick={() => refreshDashboardDatasetsMutation.mutate()}
          >
            Refresh
          </Button>
        ),
      ]}
    >
      <div className={cx("iframeContainer")}>
        <div ref={iframeRef} />
      </div>
    </Page>
  );
};
